/**
 * King Living Override - TeaserTile Hook
 *
 * Override to use 'full' variant by default to include image data
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseTeaserTileById,
  getFullTeaserTileById,
  getTeaserTileById,
} from '@amblique/contentful/app/contentful/queries';

/**
 * Hook for fetching a TeaserTile by its ID
 * King Living override: defaults to 'full' variant to include image data
 *
 * @param {string} id - The Contentful entry ID
 * @param {Object} [options={}] - Hook options
 * @param {'base'|'default'|'full'} [options.variant='full'] - Query variant
 * @param {Object} [options.variables={}] - GraphQL query variables
 * @param {Object} [options.queryOptions={}] - React Query options
 * @param {RequestInit} [options.requestOptions={}] - HTTP request options
 * @returns {import('@tanstack/react-query').UseQueryResult<Contentful.TeaserTile|null, Error>}
 */
export function useTeaserTileById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full', // King Living: default to 'full' instead of 'default'
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  // Select query based on variant
  const query = variant === 'base' ?
    getBaseTeaserTileById :
    variant === 'full' ?
      getFullTeaserTileById :
      getTeaserTileById;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'teaserTile',
      'id',
      id,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],
    queryFn: async () => {
      const data = await contentfulService.query(
        print(query),
        {id, locale: contentfulService.locale, ...variables},
        requestOptions
      );
      return data?.teaserTile ?? null;
    },
    enabled: Boolean(contentfulService && id),
    ...queryOptions,
  });
}
